/** @format */

// useMap.ts
import { useToggleZendeskWidget } from '@/@ducks/general/hooks/useToggleZendeskWidget';
import { GTMListingMapMarkerClick } from '@/@ducks/googleTagManager/helpers/google-tag-manager';
import { selectorAreas, selectorLoading, selectSearchByMap } from '@/@ducks/listing/selectors';
import { getMapCorners } from '@/@ducks/map/utils/getMapCorners';
import { TRoom } from '@/@ducks/room/declarations';
import MapPopup from '@/components/ui/Map/MapPopup';
import { RoomPriceMakersPin, SinglePin } from '@/components/ui/Map/Pin';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import React, { Fragment, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { MapRef, Marker, ViewState, ViewStateChangeEvent } from 'react-map-gl';

interface UseMapProps {
  coordinates: { latitude: number; longitude: number };
  rooms?: TRoom[];
  showMap: boolean;
  singleMarker?: boolean;
  zoom?: number;
}

export type PopupPosition =
  | 'center'
  | 'top-right'
  | 'top-left'
  | 'bottom-right'
  | 'bottom-left'
  | 'left'
  | 'right'
  | 'top'
  | 'bottom';

export type PopupAnchor = {
  position: PopupPosition;
  verticalOffset?: number;
};

export interface UseMapReturn {
  mapRef: RefObject<MapRef>;
  viewport: ViewState;
  setViewport: React.Dispatch<React.SetStateAction<ViewState>>;
  popupInfo: TRoom | null;
  setPopupInfo: React.Dispatch<React.SetStateAction<TRoom | null>>;
  pins: JSX.Element[];
  getPopupAnchor: (latitude: number, longitude: number) => PopupAnchor | undefined;
  singleMarker?: boolean;
  onViewStateChange?: Function;
  isSearchLoading?: boolean;
  onMoveEnd?: (e: ViewStateChangeEvent) => void;
}

export const useMap = ({
  coordinates,
  rooms = [],
  showMap,
  singleMarker = false,
  zoom = 12,
}: UseMapProps): UseMapReturn => {
  const dispatch = useAppDispatch();
  const mapRef = useRef<MapRef>(null);
  const [viewport, setViewport] = useState<ViewState>({
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    zoom,
    bearing: 0,
    pitch: 0,
    padding: { top: 0, bottom: 0, left: 0, right: 0 },
  });
  const [popupInfo, setPopupInfo] = useState<TRoom | null>(null);
  const isSearchLoading = useAppSelector(selectorLoading);
  const areas = useAppSelector(selectorAreas);
  const searchByMap = useAppSelector(selectSearchByMap);
  const flyToActive = useRef(false);
  useToggleZendeskWidget(!!popupInfo);

  useEffect(() => {
    const MapToggleButton = document.getElementById('MapToggleButton');
    if (!MapToggleButton) return;
    if (popupInfo) {
      MapToggleButton.style.zIndex = '-1';
    } else {
      MapToggleButton.style.zIndex = 'unset';
    }
  }, [popupInfo]);

  useEffect(() => {
    if (showMap && mapRef.current) {
      mapRef.current.resize();
    }
    setViewport(prev => ({
      ...prev,
      width: window.innerWidth,
      height: window.innerHeight,
    }));
  }, [showMap]);

  useEffect(() => {
    if (!areas) {
      setViewport({
        latitude: coordinates.latitude,
        longitude: coordinates.longitude,
        zoom,
        bearing: 0,
        pitch: 0,
        padding: { top: 0, bottom: 0, left: 0, right: 0 },
      });
    }
  }, [areas]);

  useEffect(() => {
    if (!!areas?.length && !!mapRef.current && !searchByMap) {
      const { max_longitude, max_latitude, min_longitude, min_latitude } = areas[0];
      flyToActive.current = true;
      mapRef.current.fitBounds([
        [max_longitude, min_latitude],
        [min_longitude, max_latitude],
      ]);
      mapRef.current.once('moveend', () => {
        flyToActive.current = false; // Reset flyToActive when the flyTo animation ends
      });
      dispatch({ type: 'LISTING_SET_SEARCH_BY_MAP', payload: true });
    }
  }, [areas, searchByMap, mapRef.current]);

  const getPopupAnchor = (latitude: number, longitude: number): PopupAnchor => {
    const mapBounds = mapRef.current?.getContainer().getBoundingClientRect() as { width: number; height: number };
    const popupWidth: number = 320;
    const popupHeight: number = 410;
    const popupPosition = mapRef.current?.project([longitude, latitude]) as { x: number; y: number };

    const isAbove = popupPosition?.y - popupHeight < 25 && popupPosition?.y >= 0;
    const isBelow = popupPosition?.y + popupHeight > mapBounds?.height - 25;
    const isLeft = popupPosition?.x - popupWidth < 50;
    const isRight = popupPosition?.x + popupWidth > mapBounds?.width - 50;
    const isBrowser = typeof window !== 'undefined';
    const deviceWidth = isBrowser && window.innerWidth;

    let position: PopupPosition = 'center';

    if (Number(deviceWidth) < 481) {
      return { position: 'top' };
    }

    if (isAbove && !isBelow) {
      if (isLeft === isRight) {
        position = 'top';
      } else if (isLeft) {
        position = 'top-left';
      } else if (isRight) {
        position = 'top-right';
      }
    } else if (!isAbove && isBelow) {
      if (isLeft === isRight) {
        position = 'bottom';
      } else if (isLeft) {
        position = 'bottom-left';
      } else if (isRight) {
        position = 'bottom-right';
      }
    } else if (isLeft === isRight) {
      position = 'center';
    } else if (isRight) {
      position = 'right';
    } else if (isLeft) {
      position = 'left';
    }

    let verticalOffset: number | undefined = undefined;

    if (isAbove && isBelow) {
      const aboveOffset = popupPosition?.y - popupHeight;
      const belowOffset = mapBounds?.height - popupPosition?.y - popupHeight;
      verticalOffset = (belowOffset - aboveOffset) / 2;
    }

    return { position, verticalOffset };
  };

  const pins = useMemo(() => {
    if (singleMarker) {
      return [
        <Marker key="marker-single" longitude={coordinates.longitude} latitude={coordinates.latitude} anchor="bottom">
          <SinglePin dataMarker={0} />
        </Marker>,
      ];
    }

    return rooms.map((room, index) => (
      <Fragment key={`marker-${room.code}`}>
        <Marker
          longitude={room.position.longitude}
          latitude={room.position.latitude}
          anchor="bottom"
          onClick={e => {
            e.originalEvent.stopPropagation();
            setPopupInfo(null);

            if (popupInfo && room.code === popupInfo.code) {
              return setPopupInfo(null);
            }

            const map = mapRef.current?.getMap();
            const isBrowser = typeof window !== 'undefined';
            const deviceWidth = isBrowser && window.innerWidth;

            if (Number(deviceWidth) < 481 && map) {
              flyToActive.current = true;
              map.flyTo({
                center: [room.position.longitude, room.position.latitude],
                offset: [0, -150],
                zoom: viewport.zoom,
                speed: 1.5,
                curve: 1,
                easing: t => t,
              });

              map.once('moveend', () => {
                flyToActive.current = false; // Reset flyToActive when the flyTo animation ends
              });
            }

            GTMListingMapMarkerClick(room.code, room.discountedPrice ?? room.basePrice);

            return setPopupInfo(room);
          }}
        >
          <RoomPriceMakersPin selected={popupInfo && room.code === popupInfo.code} room={room} dataMarker={index} />
        </Marker>
        {room.code === popupInfo?.code && (
          <MapPopup getPopupAnchor={getPopupAnchor} setPopupInfo={setPopupInfo} popupInfo={popupInfo} />
        )}
      </Fragment>
    ));
  }, [singleMarker, coordinates, rooms, popupInfo]);

  const onMoveEnd = (e: ViewStateChangeEvent) => {
    const eventType = e.type;
    const isNotMoveEndEvent = [undefined, 'resize'].includes(eventType);

    if (isNotMoveEndEvent) return;

    const { viewState } = e;
    setViewport(viewState);
    if (!isSearchLoading && !flyToActive.current && (window.innerWidth > 1159 || showMap)) {
      dispatch({ type: 'LISTING_SET_SEARCH_BY_MAP', payload: true });
      const mapBounds = mapRef.current?.getContainer().getBoundingClientRect() as { width: number; height: number };
      const corners = getMapCorners(viewState, mapBounds?.width, mapBounds?.height);

      const newAreas = [
        {
          min_latitude: corners.se[1],
          max_latitude: corners.nw[1],
          min_longitude: corners.se[0],
          max_longitude: corners.nw[0],
        },
      ];

      dispatch({
        type: 'LISTING_SET_MAP_AREA_FILTER',
        payload: newAreas,
      });
    }
  };

  return {
    mapRef,
    viewport,
    setViewport,
    popupInfo,
    setPopupInfo,
    pins,
    getPopupAnchor,
    isSearchLoading,
    onMoveEnd,
  };
};
